/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { get } from 'lodash';
import Lightbox from 'react-image-lightbox';
import { Icon, Spinner } from '@elements';
import AttachmentImages from '@blocks/Records/mixins/AttachmentImages';
import { useIsMobile, saveToDeviceDownloadFolder } from '@helpers/helpers';
import { localPreviewFor } from '@helpers/imageUpload';

import './ImageView.scss';

type Props = {
  onClickDelete?: Function,
  onClickDownload?: Function,
  images: Array<{
    id: number,
    thumbUrl: string,
    largeUrl: string,
    name: string,
  }>,
}

// A photo uploaded in this session opens from the device: the backend may still be making its large version
const largeSrc = (image: ?Object): string => {
  const localPreview = localPreviewFor(get(image, 'id'));
  return localPreview ? localPreview.largeUrl : get(image, 'largeUrl', '');
};

const Gallery = ({ images = [], onClickDelete, onClickDownload }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [imgIndex, setImgIndex] = useState(0);
  const [isSharing, setIsSharing] = useState(false);

  const [isMobile] = useIsMobile();

  const onOpenLightbox = (id) => () => {
    const _imgIndex = images.findIndex(img => img.id === id);
    // A tile with no id (a photo the app has no details for) has nothing to show, and its trash deleted "no id"
    if (!id || _imgIndex < 0) return;

    setImgIndex(_imgIndex);
    setIsOpen(true);
  };

  const onCloseLightbox = () => {
    setIsOpen(false);
  };

  const onClickNextImg = () => {
    setImgIndex(state => state + 1);
  };

  const onClickPrevImg = () => {
    setImgIndex(state => state - 1);
  };

  const onClickDeleteImg = () => {
    if (!onClickDelete) return;

    const imgId = get(images[imgIndex], 'id');
    // Never a DELETE without an id: it went out as DELETE /records/:id/attachments/, a 404 (Bugsnag 6ae10d2b…)
    if (!imgId) return;

    onClickDelete(imgId)();
  };

  const onClickShare = () => {
    setIsSharing(true);
    onClickDownload(get(images[imgIndex], 'id', null), (url: string | null) => {
      if (!url) {
        setIsSharing(false);
        return;
      }

      const onCatch = (e) => {
        // eslint-disable-next-line
        console.error(e);
        setIsSharing(false);
      };
      // eslint-disable-next-line
      const filename = `${(new Date()).getTime()}-${url.replace(/[\#\?].*$/, '').replace(/^.*[\\\/]/, '')}`;
      fetch(url, { method: 'GET' })
        .then(res => res.blob().then((blob) => {
          saveToDeviceDownloadFolder(blob, filename).then(() => {
            setIsSharing(false);
          }).catch(onCatch);
        }).catch(onCatch))
        .catch(onCatch);
    });
  };

  const getToolbarButtons = () => {
    const getDeleteButton = () => {
      if (!onClickDelete) return null;

      return (
        <Icon iconName='delete' className='margin6px' size={23} onClick={onClickDeleteImg} />
      );
    };


    const getSharingButton = () => {
      if (!isMobile || !onClickDownload) return null;

      if (isSharing) return <Spinner className='margin6px' fill="#fff" />;

      return (
        <Icon
          iconName="share"
          className='margin6px'
          size={23}
          onClick={onClickShare}
        />
      );
    };

    const getDownloadButton = () => {
      if (!onClickDownload) return null;

      const downloadUrl = get(images[imgIndex], 'downloadUrl', '');
      const name = get(images[imgIndex], 'name', '');

      return (
        <a href={downloadUrl} download={name} target="_blank">
          <Icon
            iconName='download'
            className='margin6px'
            size={23}
          />
        </a>
      );
    };

    // A photo with no id has nothing to delete, share or download
    if (!get(images[imgIndex], 'id')) return [];

    const buttons = [getDeleteButton(), getSharingButton(), getDownloadButton()];

    return buttons.filter(Boolean);
  };

  const mainSrc = largeSrc(images[imgIndex]);

  useEffect(() => {
    if (mainSrc) return;

    setIsOpen(false);
  }, [mainSrc]);

  // The lightbox hears keys only while it has the focus: on the first or last photo its arrow goes away and takes the
  // focus with it, and Escape and the arrow keys stopped working
  useEffect(() => {
    if (!isOpen) return;
    const outer = document.querySelector('.ril-outer');
    if (outer && !outer.contains(document.activeElement)) outer.focus();
  }, [isOpen, imgIndex]);

  return (
    <>
      <AttachmentImages recordImages={images} openLightBox={onOpenLightbox} />

      {isOpen && (
        <Lightbox
          mainSrc={mainSrc}
          nextSrc={largeSrc(images[imgIndex + 1])}
          prevSrc={largeSrc(images[imgIndex - 1])}
          toolbarButtons={getToolbarButtons()}
          wrapperClassName={isMobile ? 'mobileAppLightbox' : ''}
          onCloseRequest={onCloseLightbox}
          onMovePrevRequest={onClickPrevImg}
          onMoveNextRequest={onClickNextImg}
        />
      )}
    </>
  );
};

export default Gallery;
